import { useEffect, useState } from "react";

// Tailwind's default breakpoints. Keep these in sync with tailwind.config.js
// so JS-driven layout decisions match CSS utility classes.
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

function read() {
  // SSR-safe — server has no window. CRA isn't SSR but stay defensive.
  if (typeof window === "undefined") {
    return { sm: true, md: true, lg: true, xl: true, isMobile: false, isTablet: false, isDesktop: true, width: 1280 };
  }
  const w = window.innerWidth;
  return {
    sm: w >= BREAKPOINTS.sm,
    md: w >= BREAKPOINTS.md,
    lg: w >= BREAKPOINTS.lg,
    xl: w >= BREAKPOINTS.xl,
    // Friendlier aliases — "mobile" = below tablet, "tablet" = md to lg, "desktop" = lg and above.
    isMobile: w < BREAKPOINTS.md,
    isTablet: w >= BREAKPOINTS.md && w < BREAKPOINTS.lg,
    isDesktop: w >= BREAKPOINTS.lg,
    width: w,
  };
}

// Returns the current breakpoint snapshot. Listens to window resize via
// matchMedia for cheap updates (no per-pixel rerender).
export default function useBreakpoint() {
  const [state, setState] = useState(read);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => setState(read());

    // One MediaQueryList per breakpoint so we only fire when crossing a
    // boundary that matters.
    const mqls = Object.values(BREAKPOINTS).map((px) => window.matchMedia(`(min-width: ${px}px)`));
    mqls.forEach((mql) => mql.addEventListener("change", update));

    // Also catch the initial mount in case the first render's read() was
    // wrong (rare, but cheap to belt-and-braces).
    update();

    return () => {
      mqls.forEach((mql) => mql.removeEventListener("change", update));
    };
  }, []);

  return state;
}

export { BREAKPOINTS };
